import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { industryService, type CreateIndustryData, type UpdateIndustryData } from '../services/industryService';
import { useToast } from '@/hooks/use-toast';

export const industryKeys = {
  all: ['industries'] as const,
  lists: () => [...industryKeys.all, 'list'] as const,
  detail: (id: string) => [...industryKeys.all, 'detail', id] as const,
};

export function useIndustries() {
  return useQuery({
    queryKey: industryKeys.lists(),
    queryFn: industryService.getIndustries,
    staleTime: 0, // Always refetch to ensure fresh data
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  });
}

export function useCreateIndustry() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: industryService.createIndustry,
    onMutate: async (newIndustry: CreateIndustryData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: industryKeys.lists() });

      // Snapshot the previous value
      const previousIndustries = queryClient.getQueryData(industryKeys.lists());

      // Optimistically update
      const optimisticIndustry = {
        id: 'temp-' + Date.now(),
        name: newIndustry.name,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        org_id: null,
        created_by: null,
      };

      queryClient.setQueryData(industryKeys.lists(), (old: any) => 
        old ? [...old, optimisticIndustry] : [optimisticIndustry]
      );

      return { previousIndustries };
    },
    onError: (err, newIndustry, context) => {
      queryClient.setQueryData(industryKeys.lists(), context?.previousIndustries);
      toast({
        title: "Error",
        description: "Failed to create industry. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Industry created successfully.",
      });
      // Force refetch across all components
      queryClient.invalidateQueries({ queryKey: industryKeys.lists(), refetchType: 'all' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: industryKeys.lists() });
    },
  });
}

export function useUpdateIndustry() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: industryService.updateIndustry,
    onMutate: async (updatedIndustry: UpdateIndustryData) => {
      await queryClient.cancelQueries({ queryKey: industryKeys.lists() });
      
      const previousIndustries = queryClient.getQueryData(industryKeys.lists());

      queryClient.setQueryData(industryKeys.lists(), (old: any) =>
        old ? old.map((industry: any) =>
          industry.id === updatedIndustry.id
            ? { ...industry, ...updatedIndustry, updated_at: new Date().toISOString() }
            : industry
        ) : []
      );

      return { previousIndustries };
    },
    onError: (err, updatedIndustry, context) => {
      queryClient.setQueryData(industryKeys.lists(), context?.previousIndustries);
      toast({
        title: "Error",
        description: "Failed to update industry. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Industry updated successfully.",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: industryKeys.lists() });
    },
  });
}

export function useDeleteIndustry() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: industryService.deleteIndustry,
    onMutate: async (industryId: string) => {
      await queryClient.cancelQueries({ queryKey: industryKeys.lists() });
      
      const previousIndustries = queryClient.getQueryData(industryKeys.lists());

      queryClient.setQueryData(industryKeys.lists(), (old: any) =>
        old ? old.filter((industry: any) => industry.id !== industryId) : []
      );

      return { previousIndustries };
    },
    onError: (err, industryId, context) => {
      queryClient.setQueryData(industryKeys.lists(), context?.previousIndustries);
      toast({
        title: "Error",
        description: "Failed to delete industry. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Industry deleted successfully.",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: industryKeys.lists() });
    },
  });
}